import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CURRENT_SEASON } from '@/lib/season';
import { isFinished } from '@/lib/fixture-status';

interface Fixture {
  id: string;
  fixture_id: number;
  home_team: string;
  away_team: string;
  matchday: number;
  season: string;
  kickoff_time: string;
  venue?: string;
  status: string;
  elapsed?: number | null;
  home_score?: number;
  away_score?: number;
  winner?: string | null;
  home_win_odds?: number;
  draw_odds?: number;
  away_win_odds?: number;
  bookmaker?: string;
  last_updated: string;
}

/** Status bucket: 0 = live/upcoming (not finished), 1 = finished */
function statusBucket(status: string): number {
  if (isFinished(status)) return 1;
  return 0; // live + upcoming grouped together
}

/** Sort: live + upcoming by kickoff asc, then finished by kickoff asc */
function sortFixtures(fixtures: Fixture[]): Fixture[] {
  return [...fixtures].sort((a, b) => {
    const bucketDiff = statusBucket(a.status) - statusBucket(b.status);
    if (bucketDiff !== 0) return bucketDiff;
    return new Date(a.kickoff_time).getTime() - new Date(b.kickoff_time).getTime();
  });
}

/**
 * Filter to only the most recently synced batch of fixtures.
 * The DB may have stale rows from previous syncs with different fixture_ids
 * that were incorrectly tagged with this matchday. The latest sync batch
 * (all updated within minutes of each other) contains the correct data.
 */
function filterToLatestBatch(fixtures: Fixture[]): Fixture[] {
  if (fixtures.length <= 10) return sortFixtures(fixtures);

  // Find the most recent last_updated timestamp
  const maxUpdated = fixtures.reduce(
    (max, f) => (f.last_updated > max ? f.last_updated : max),
    fixtures[0].last_updated
  );

  // Keep only fixtures updated within 10 minutes of the newest one
  // (a sync batch processes all fixtures together, so they'll be within seconds)
  const cutoff = new Date(new Date(maxUpdated).getTime() - 10 * 60 * 1000).toISOString();
  const filtered = fixtures.filter((f) => f.last_updated >= cutoff);

  // Sanity: if filtering is too aggressive, fall back to all
  if (filtered.length < 5) return fixtures;

  return sortFixtures(filtered);
}

export const useCurrentMatchdayFixtures = (matchday: number = 1, season: string = CURRENT_SEASON, competitionId?: number) => {
  return useQuery({
    queryKey: ['current-matchday-fixtures', matchday, season, competitionId],
    enabled: competitionId != null,
    queryFn: async () => {
      let query = supabase
        .from('fixtures')
        .select('*')
        .eq('season', season)
        .eq('matchday', matchday)
        .order('kickoff_time', { ascending: true });
      if (competitionId != null) query = query.eq('competition_id', competitionId);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching current matchday fixtures:', error);
        throw error;
      }

      const fixtures = data as Fixture[];

      // Filter to latest sync batch — the DB may have stale rows from previous syncs
      const filtered = filterToLatestBatch(fixtures);
      if (filtered.length !== fixtures.length) {
        console.warn(
          `[Fixtures] Filtered MD ${matchday}: ${fixtures.length} rows → ${filtered.length} (removed stale rows)`
        );
      }

      return filtered;
    },
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    // Auto-refetch every 60s to keep scores and statuses up to date
    // (the watch mode relies on this for live fixture updates)
    refetchInterval: 60 * 1000,
  });
};
